import { useAuth } from "@clerk/react";
import { useCallback, useEffect, useRef, useState } from "react";

import { fetchConnectionState } from "../lib/marketplace-connection-api";
import type {
  BuyerConnectionDTO,
  ConnectionRequestDTO,
} from "../lib/marketplace-status";

/**
 * Live buyer-side connection read for a role (M6-C → M8 wiring, collapsing the
 * artificial M8 deferral: the M6-B connection engine already ships in this
 * branch, so the consumer surface can read it directly).
 *
 * Polls `GET /api/marketplace/connection?role=` while the connection is still
 * finalizing (`charging`/`held`) so the buyer watches the Temporal
 * charge→reveal payoff land without a manual reload. Polling stops the moment
 * the connection resolves to `active`, a terminal status, or `null`.
 *
 * Poll cadence is deliberately gentle — the reveal is "usually within a day"
 * product copy, but locally (sandbox charge) it lands in seconds, so a few-
 * second cadence gives a live-feeling transition without hammering the API.
 *
 * Plan 435 M0/M1 (mfh-fe-withdraw-api / mfh-home-settle): `pending_request`
 * rides the SAME `GET /api/marketplace/connection?role=` response as
 * `connection` (both read via `fetchConnectionState`, one `api.get` call),
 * so this hook now also exposes the buyer's pending standing-offer request —
 * callers that only needed `connection` before are unaffected.
 */
const POLL_MS = 4000;

function isFinalizing(conn: BuyerConnectionDTO | null): boolean {
  return conn !== null && (conn.status === "charging" || conn.status === "held");
}

export interface UseMarketplaceConnectionOptions {
  /**
   * Plan 435 M1 (mfh-home-settle): Home must issue ZERO marketplace reads
   * while the consumer marketplace gate is "loading"/"off". Hook rules
   * require this to always be called, so the enable/disable toggle lives
   * here rather than in a conditional call site. Defaults to `true` so
   * every pre-existing caller is unaffected.
   */
  enabled?: boolean;
}

export interface UseMarketplaceConnectionResult {
  connection: BuyerConnectionDTO | null;
  /** null = no pending standing-offer request for this role (or not yet loaded). */
  pendingRequest: ConnectionRequestDTO | null;
  refresh: () => Promise<void>;
  /**
   * True once the first read has resolved or failed (or immediately when
   * `enabled: false` — there is nothing to wait for). Settle-gate input for
   * Home (mfh-home-settle); pre-existing callers can ignore it.
   */
  settled: boolean;
}

export function useMarketplaceConnection(
  role: string,
  options: UseMarketplaceConnectionOptions = {},
): UseMarketplaceConnectionResult {
  const enabled = options.enabled ?? true;
  const { getToken } = useAuth();
  const [connection, setConnection] = useState<BuyerConnectionDTO | null>(null);
  const [pendingRequest, setPendingRequest] =
    useState<ConnectionRequestDTO | null>(null);
  const [settled, setSettled] = useState(!enabled);
  // Guards state writes from an in-flight fetch after unmount / role change.
  const activeRef = useRef(true);
  // Clerk's `getToken` identity is unstable across renders; hold it in a ref
  // (synced in an effect, never during render) so the poll effect / `refresh`
  // depend only on `role` and don't tear down and re-fetch on every unrelated
  // re-render. The async readers below always read `.current`, so they see the
  // latest token.
  const getTokenRef = useRef(getToken);
  useEffect(() => {
    getTokenRef.current = getToken;
  });

  const refresh = useCallback(async () => {
    if (!enabled) return;
    try {
      const result = await fetchConnectionState(getTokenRef.current, role);
      if (activeRef.current) {
        setConnection(result.connection);
        setPendingRequest(result.pendingRequest);
      }
    } catch {
      // Transient read failure — keep the prior value rather than flashing
      // the buyer back to a no-connection state.
    }
  }, [role, enabled]);

  useEffect(() => {
    if (!enabled) {
      let cancelled = false;
      // Deferred past a microtask — calling setState synchronously inside
      // an effect body trips the react-hooks/set-state-in-effect lint;
      // mirrors the existing `await Promise.resolve()` lead-in idiom used
      // elsewhere in this codebase for the identical shape.
      void Promise.resolve().then(() => {
        if (!cancelled) setSettled(true);
      });
      return () => {
        cancelled = true;
      };
    }

    activeRef.current = true;
    // Deferred past a microtask (react-hooks/set-state-in-effect) — fires
    // once per mount/role-change, BEFORE `tick()`'s own await resolves, so
    // it can never race the `finally` below. Deliberately NOT reset per
    // poll tick: a background poll while finalizing must not flash Home's
    // settle gate back to its skeleton every 4s.
    void Promise.resolve().then(() => setSettled(false));
    let timer: ReturnType<typeof setTimeout> | undefined;

    const tick = async () => {
      let conn: BuyerConnectionDTO | null = null;
      try {
        const result = await fetchConnectionState(getTokenRef.current, role);
        conn = result.connection;
        if (!activeRef.current) return;
        setConnection(result.connection);
        setPendingRequest(result.pendingRequest);
      } catch {
        // Swallow — reschedule below so a blip doesn't strand the poller.
      } finally {
        if (activeRef.current) setSettled(true);
      }
      if (activeRef.current && isFinalizing(conn)) {
        timer = setTimeout(() => void tick(), POLL_MS);
      }
    };

    void tick();

    return () => {
      activeRef.current = false;
      if (timer) clearTimeout(timer);
    };
  }, [role, enabled]);

  return { connection, pendingRequest, refresh, settled };
}
